import { useEffect, useRef, useState } from 'react';

interface UseElementDimensionsProps {
    debounceTime?: number;
}

const useElementDimensions = ({
    debounceTime = 200
}: UseElementDimensionsProps) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [elementDimensions, setElementDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    useEffect(() => {
        const debounce = (func: (...args: any[]) => void, wait: number) => {
            let timeout: NodeJS.Timeout | undefined;
            return (...args: any[]) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(null, args), wait);
            };
        };

        const calculateDimensions = () => {
            if (elementRef?.current) {
                const newDimensions = {
                    width: elementRef?.current?.offsetWidth * 0.95,
                    height: elementRef?.current?.offsetHeight * 0.95
                };
                setElementDimensions(newDimensions);
            }
        };

        const debouncedCalculateDimensions = debounce(calculateDimensions, debounceTime || 200);

        const handleResize = () => {
            debouncedCalculateDimensions();
        };

        window.addEventListener('widgetResize', handleResize);
        window.addEventListener('resize', handleResize);

        let resizeObserver: ResizeObserver | null = null;
        if (elementRef.current) {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(elementRef.current);
        }

        handleResize();

        return () => {
            window.removeEventListener('widgetResize', handleResize);
            window.removeEventListener('resize', handleResize);
            if (resizeObserver && elementRef.current) {
                resizeObserver.unobserve(elementRef.current);
            }
        };
    }, []);

    return {
        elementRef,
        elementDimensions
    };
};

export default useElementDimensions;